import React from 'react';
import * as Icons from '../../icons';

/**
 * IconShowcase
 * Displays all available icons organized by category
 */
import { surfaces, textColors, typography, borders } from '../../tokens';
const IconShowcase = () => {
  const CodeBlock = ({ code }) => (
    <pre className="bg-gray-900 dark:bg-dark-950 text-gray-100 dark:text-dark-200 p-4 rounded-lg text-xs overflow-x-auto">
      <code>{code}</code>
    </pre>
  );

  const IconCard = ({ icon: Icon, name }) => (
    <div className="flex flex-col items-center p-4 ${surfaces.primary} rounded-lg border ${borders.default} hover:border-blue-500 hover:shadow-sm transition-all">
      <Icon size="lg" color="primary" />
      <span className="text-xs ${textColors.secondary} mt-2 text-center">{name}</span>
    </div>
  );

  // Common Icons
  const commonIcons = [
    { icon: Icons.SearchIcon, name: 'SearchIcon' },
    { icon: Icons.CheckIcon, name: 'CheckIcon' },
    { icon: Icons.XIcon, name: 'XIcon' },
    { icon: Icons.ChevronDownIcon, name: 'ChevronDownIcon' },
    { icon: Icons.ChevronUpIcon, name: 'ChevronUpIcon' },
    { icon: Icons.ChevronLeftIcon, name: 'ChevronLeftIcon' },
    { icon: Icons.ChevronRightIcon, name: 'ChevronRightIcon' },
    { icon: Icons.GlobeIcon, name: 'GlobeIcon' },
  ];

  // Navigation Icons
  const navigationIcons = [
    { icon: Icons.DashboardIcon, name: 'DashboardIcon' },
    { icon: Icons.MessageIcon, name: 'MessageIcon' },
    { icon: Icons.HomeIcon, name: 'HomeIcon' },
    { icon: Icons.ArrowLeftIcon, name: 'ArrowLeftIcon' },
    { icon: Icons.ArrowRightIcon, name: 'ArrowRightIcon' },
  ];

  // Action Icons
  const actionIcons = [
    { icon: Icons.EditIcon, name: 'EditIcon' },
    { icon: Icons.PencilIcon, name: 'PencilIcon' },
    { icon: Icons.TrashIcon, name: 'TrashIcon' },
    { icon: Icons.CopyIcon, name: 'CopyIcon' },
    { icon: Icons.PublishIcon, name: 'PublishIcon' },
    { icon: Icons.DraftIcon, name: 'DraftIcon' },
    { icon: Icons.AnalyticsIcon, name: 'AnalyticsIcon' },
    { icon: Icons.ExternalLinkIcon, name: 'ExternalLinkIcon' },
    { icon: Icons.ContinueIcon, name: 'ContinueIcon' },
    { icon: Icons.PlusIcon, name: 'PlusIcon' },
    { icon: Icons.SettingsDotsIcon, name: 'SettingsDotsIcon' },
  ];

  // Status Icons
  const statusIcons = [
    { icon: Icons.SuccessIcon, name: 'SuccessIcon' },
    { icon: Icons.ErrorIcon, name: 'ErrorIcon' },
    { icon: Icons.WarningIcon, name: 'WarningIcon' },
    { icon: Icons.InfoIcon, name: 'InfoIcon' },
    { icon: Icons.AlertIcon, name: 'AlertIcon' },
  ];

  // Settings Icons
  const settingsIcons = [
    { icon: Icons.SettingsDotsIcon, name: 'SettingsDotsIcon' },
    { icon: Icons.AccountIcon, name: 'AccountIcon' },
    { icon: Icons.PreferencesIcon, name: 'PreferencesIcon' },
    { icon: Icons.SecurityIcon, name: 'SecurityIcon' },
    { icon: Icons.NotificationsIcon, name: 'NotificationsIcon' },
    { icon: Icons.SubscriptionIcon, name: 'SubscriptionIcon' },
    { icon: Icons.LogoutIcon, name: 'LogoutIcon' },
  ];

  // Social Icons
  const socialIcons = [
    { icon: Icons.GoogleIcon, name: 'GoogleIcon' },
    { icon: Icons.ShareIcon, name: 'ShareIcon' },
    { icon: Icons.FeedbackIcon, name: 'FeedbackIcon' },
  ];

  // Sorting Icons
  const sortingIcons = [
    { icon: Icons.SortOrderIcon, name: 'SortOrderIcon' },
    { icon: Icons.SortAlphaIcon, name: 'SortAlphaIcon' },
  ];

  return (
    <div className="space-y-12">
      {/* Icon Sizes */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-4`}>Icon Sizes</h2>
        <p className={`${typography.body} ${textColors.secondary} mb-6`}>
          All icons support consistent sizing from xs to 3xl.
        </p>

        <div className="flex flex-wrap items-end gap-6 p-6 ${surfaces.primary} rounded-lg border ${borders.default} mb-6">
          <div className="flex flex-col items-center">
            <Icons.SearchIcon size="xs" color="primary" />
            <span className="text-xs ${textColors.secondary} mt-2">xs</span>
          </div>
          <div className="flex flex-col items-center">
            <Icons.SearchIcon size="sm" color="primary" />
            <span className="text-xs ${textColors.secondary} mt-2">sm</span>
          </div>
          <div className="flex flex-col items-center">
            <Icons.SearchIcon size="md" color="primary" />
            <span className="text-xs ${textColors.secondary} mt-2">md</span>
          </div>
          <div className="flex flex-col items-center">
            <Icons.SearchIcon size="lg" color="primary" />
            <span className="text-xs ${textColors.secondary} mt-2">lg</span>
          </div>
          <div className="flex flex-col items-center">
            <Icons.SearchIcon size="xl" color="primary" />
            <span className="text-xs ${textColors.secondary} mt-2">xl</span>
          </div>
          <div className="flex flex-col items-center">
            <Icons.SearchIcon size="2xl" color="primary" />
            <span className="text-xs ${textColors.secondary} mt-2">2xl</span>
          </div>
          <div className="flex flex-col items-center">
            <Icons.SearchIcon size="3xl" color="primary" />
            <span className="text-xs ${textColors.secondary} mt-2">3xl</span>
          </div>
        </div>
      </section>

      {/* Icon Colors */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-4`}>Icon Colors</h2>
        <p className={`${typography.body} ${textColors.secondary} mb-6`}>
          Icons support semantic color variants.
        </p>

        <div className="flex flex-wrap items-center gap-6 p-6 ${surfaces.primary} rounded-lg border ${borders.default} mb-6">
          <div className="flex flex-col items-center">
            <Icons.CheckIcon size="xl" color="primary" />
            <span className="text-xs ${textColors.secondary} mt-2">primary</span>
          </div>
          <div className="flex flex-col items-center">
            <Icons.CheckIcon size="xl" color="secondary" />
            <span className="text-xs ${textColors.secondary} mt-2">secondary</span>
          </div>
          <div className="flex flex-col items-center">
            <Icons.CheckIcon size="xl" color="success" />
            <span className="text-xs ${textColors.secondary} mt-2">success</span>
          </div>
          <div className="flex flex-col items-center">
            <Icons.CheckIcon size="xl" color="warning" />
            <span className="text-xs ${textColors.secondary} mt-2">warning</span>
          </div>
          <div className="flex flex-col items-center">
            <Icons.CheckIcon size="xl" color="danger" />
            <span className="text-xs ${textColors.secondary} mt-2">danger</span>
          </div>
          <div className="flex flex-col items-center">
            <Icons.CheckIcon size="xl" color="neutral" />
            <span className="text-xs ${textColors.secondary} mt-2">neutral</span>
          </div>
        </div>
      </section>

      {/* Common Icons */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-4`}>Common Icons</h2>
        <p className={`${typography.body} ${textColors.secondary} mb-6`}>
          General-purpose icons used throughout the application.
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {commonIcons.map(({ icon, name }) => (
            <IconCard key={name} icon={icon} name={name} />
          ))}
          <IconCard icon={Icons.LoadingSpinner} name="LoadingSpinner" />
        </div>
      </section>

      {/* Navigation Icons */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-4`}>Navigation Icons</h2>
        <p className={`${typography.body} ${textColors.secondary} mb-6`}>
          Icons for navigation and wayfinding.
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {navigationIcons.map(({ icon, name }) => (
            <IconCard key={name} icon={icon} name={name} />
          ))}
        </div>
      </section>

      {/* Action Icons */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-4`}>Action Icons</h2>
        <p className={`${typography.body} ${textColors.secondary} mb-6`}>
          Icons representing user actions.
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {actionIcons.map(({ icon, name }) => (
            <IconCard key={name} icon={icon} name={name} />
          ))}
        </div>
      </section>

      {/* Status Icons */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-4`}>Status Icons</h2>
        <p className={`${typography.body} ${textColors.secondary} mb-6`}>
          Icons for different status states.
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {statusIcons.map(({ icon, name }) => (
            <IconCard key={name} icon={icon} name={name} />
          ))}
        </div>
      </section>

      {/* Settings Icons */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-4`}>Settings Icons</h2>
        <p className={`${typography.body} ${textColors.secondary} mb-6`}>
          Icons for settings and configuration.
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {settingsIcons.map(({ icon, name }) => (
            <IconCard key={name} icon={icon} name={name} />
          ))}
        </div>
      </section>

      {/* Social Icons */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-4`}>Social Icons</h2>
        <p className={`${typography.body} ${textColors.secondary} mb-6`}>
          Icons for social media and communication.
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {socialIcons.map(({ icon, name }) => (
            <IconCard key={name} icon={icon} name={name} />
          ))}
        </div>
      </section>

      {/* Sorting Icons */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-4`}>Sorting Icons</h2>
        <p className={`${typography.body} ${textColors.secondary} mb-6`}>
          Icons for sorting and ordering.
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {sortingIcons.map(({ icon, name }) => (
            <IconCard key={name} icon={icon} name={name} />
          ))}
        </div>
      </section>

      {/* Usage Example */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-4`}>Usage Examples</h2>
        
        <div className="mb-6">
          <h3 className={`${typography.h4} ${textColors.primary} mb-3`}>Basic Usage</h3>
          <CodeBlock code={`import { SearchIcon } from '../design-system';

<SearchIcon size="md" color="primary" />`} />
        </div>

        <div className="mb-6">
          <h3 className={`${typography.h4} ${textColors.primary} mb-3`}>In Buttons</h3>
          <CodeBlock code={`import { PrimaryButton, PlusIcon } from '../design-system';

<PrimaryButton leftIcon={<PlusIcon size="sm" />}>
  Create New
</PrimaryButton>`} />
        </div>

        <div className="mb-6">
          <h3 className={`${typography.h4} ${textColors.primary} mb-3`}>With Custom Classes</h3>
          <CodeBlock code={`<CheckIcon 
  size="xl" 
  className="text-green-500 hover:text-green-600" 
/>`} />
        </div>
      </section>

      {/* Ant Design Icons Note */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-4`}>Ant Design Icons</h2>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <p className="${textColors.primary} mb-2">
            <strong>Note:</strong> The project also uses some Ant Design icons for specific purposes:
          </p>
          <ul className="list-disc list-inside ${textColors.secondary} space-y-1 mt-3">
            <li>Device preview icons (MobileOutlined, TabletOutlined, LaptopOutlined, DesktopOutlined)</li>
            <li>Navigation management icons (specific to edit navbar)</li>
            <li>AI-specific icons (in AIIcons.js)</li>
          </ul>
          <p className="text-sm ${textColors.secondary} mt-3">
            These are kept separate as they're highly specialized or part of existing Ant Design integration.
          </p>
        </div>
      </section>
    </div>
  );
};

export default IconShowcase;
